/**
 * PickupPanelPage — monitor for pickup/takeaway orders.
 *
 * Two columns: PENDING and BEING SERVED. The cashier taps
 * "Ready for Pickup" or "Served" to advance an order.
 *
 * Saheeh port: firebase/firestore → firestoreCompat (Supabase),
 * branch filter removed (single tenant).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Button, IconButton, Snackbar, Alert } from '@mui/material';
import {
  AccessTime as ClockIcon, CheckCircleOutline as CheckIcon,
  ArrowForward as ArrowIcon, GridView as GridIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import {
  collection, onSnapshot, doc, updateDoc, getFirestore, serverTimestamp,
  query, where,
} from '../../services/firestoreCompat';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';

interface PickupOrder {
  id: string;
  order_number: string;
  pickup_code: string;
  customer_name: string;
  total: number;
  platform: string;
  status: string;
  payment_status: string;
  created_at: Date;
}

const C = {
  surface: '#252838',
  surfaceAlt: '#1e2139',
  border: '#3a3d52',
  accent: '#94e815',
  text: '#FFFFFF',
  textMuted: '#cbd5e1',
  textDim: '#94a3b8',
  pending: '#F97316',
  served: '#3B82F6',
  done: '#22C55E',
};

const PickupPanelPage: React.FC = () => {
  const { language } = useLanguage();
  const isDe = false; // Saheeh: en/ar only
  void language;
  const navigate = useNavigate();

  const [orders, setOrders] = useState<PickupOrder[]>([]);
  const [snack, setSnack] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({ open: false, msg: '', sev: 'success' });

  // Live listener: today's pickup/counter/takeaway orders.
  useEffect(() => {
    const db = getFirestore();
    const startOfDay = new Date(); startOfDay.setHours(0, 0, 0, 0);
    const q = query(
      collection(db, 'orders'),
      where('created_at', '>=', startOfDay.toISOString()),
    );
    const unsub = onSnapshot(q, (snap) => {
      const list: PickupOrder[] = [];
      snap.forEach(d => {
        const data = d.data() as any;
        const type = (data.order_type || '').toLowerCase();
        if (!['pickup', 'counter', 'takeaway'].includes(type)) return;
        const status = (data.status || '').toLowerCase();
        if (status === 'cancelled' || status === 'completed' || status === 'delivered') return;
        const ca = data.created_at ? new Date(data.created_at) : new Date();
        list.push({
          id: d.id,
          order_number: String(data.order_number || `#${d.id.slice(-5)}`),
          pickup_code: (data.pickup_code || d.id.slice(-6).toUpperCase()),
          customer_name: data.customer_name || (isDe ? 'Laufkunde' : 'Walk-in'),
          total: Number(data.total || 0),
          platform: (data.platform || data.source || 'pos').toLowerCase(),
          status,
          payment_status: (data.payment_status || 'pending').toLowerCase(),
          created_at: ca,
        });
      });
      list.sort((a, b) => a.created_at.getTime() - b.created_at.getTime());
      setOrders(list);
    });
    return () => unsub();
  }, [isDe]);

  const pending = useMemo(() => orders.filter(o => o.status !== 'serving'), [orders]);
  const serving = useMemo(() => orders.filter(o => o.status === 'serving'), [orders]);

  const markReady = async (o: PickupOrder) => {
    try {
      const db = getFirestore();
      await updateDoc(doc(db, 'orders', o.id), { status: 'serving', updated_at: serverTimestamp() });
      setSnack({ open: true, sev: 'success', msg: (isDe ? 'Bereit zur Abholung: ' : 'Ready for pickup: ') + o.pickup_code });
    } catch {
      setSnack({ open: true, sev: 'error', msg: isDe ? 'Fehler' : 'Failed' });
    }
  };

  const markServed = async (o: PickupOrder) => {
    try {
      const db = getFirestore();
      await updateDoc(doc(db, 'orders', o.id), { status: 'completed', updated_at: serverTimestamp() });
      setSnack({ open: true, sev: 'success', msg: (isDe ? 'Abgeschlossen: ' : 'Completed: ') + o.pickup_code });
    } catch {
      setSnack({ open: true, sev: 'error', msg: isDe ? 'Fehler' : 'Failed' });
    }
  };

  const renderCard = (o: PickupOrder, mode: 'pending' | 'serving') => (
    <Box key={o.id} sx={{
      backgroundColor: C.surface,
      border: `2px solid ${mode === 'pending' ? C.pending : C.served}`,
      borderRadius: '16px', p: 1.6,
      display: 'flex', flexDirection: 'column', gap: 1.2,
      minHeight: 220,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{
          minWidth: 36, height: 28, px: 1, borderRadius: '8px',
          backgroundColor: '#1a1d2e', color: C.text,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 800, fontSize: '1rem',
        }}>{o.order_number}</Box>
        <Box sx={{
          flex: 1, backgroundColor: '#FFFFFF', color: '#1a1d2e',
          textAlign: 'center', fontWeight: 800, fontSize: '1.05rem',
          py: 0.4, borderRadius: '8px', letterSpacing: '0.04em',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        }}>{o.pickup_code}</Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ color: C.text, fontWeight: 600, fontSize: '0.95rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {o.customer_name}
        </Typography>
        <Typography sx={{ color: C.accent, fontWeight: 800, fontSize: '0.95rem' }}>
          {o.total.toFixed(2)} SAR
        </Typography>
      </Box>

      <Button
        onClick={() => mode === 'pending' ? markReady(o) : markServed(o)}
        endIcon={mode === 'pending' ? <ArrowIcon /> : <CheckIcon />}
        sx={{
          backgroundColor: mode === 'pending' ? '#22C55E' : '#3B82F6',
          color: '#FFFFFF', borderRadius: '12px',
          py: 1.4, textTransform: 'none', fontWeight: 800, fontSize: '1rem',
          '&:hover': { backgroundColor: mode === 'pending' ? '#16A34A' : '#2563EB' },
        }}
      >
        {mode === 'pending'
          ? (isDe ? 'Bereit zur Abholung' : 'Ready for Pickup')
          : (isDe ? 'Abgeschlossen' : 'Served')}
      </Button>

      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.8,
        py: 1, borderRadius: '10px', backgroundColor: C.surfaceAlt,
        border: `1px solid ${C.border}`, color: C.textMuted,
        fontWeight: 600, fontSize: '0.9rem',
      }}>
        <GridIcon sx={{ fontSize: 18 }} />
        {isDe ? 'Sonstiges' : 'Other'}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, minHeight: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 2 }}>
        <IconButton onClick={() => navigate('/delivery')} sx={{ color: C.accent, border: `1px solid ${C.border}`, borderRadius: '10px' }}>
          <BackIcon />
        </IconButton>
        <Typography sx={{ color: C.text, fontWeight: 800, fontSize: '1.3rem' }}>
          {isDe ? 'Abholtafel' : 'Pickup Panel'}
        </Typography>
      </Box>

      <Box sx={{
        display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: { xs: 2, md: 3 },
      }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.2, mb: 2 }}>
            <ClockIcon sx={{ color: C.pending, fontSize: 26 }} />
            <Typography sx={{ color: C.text, fontWeight: 800, fontSize: '1.4rem' }}>
              {isDe ? 'AUSSTEHEND' : 'PENDING'}: {pending.length}
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }, gap: 1.5 }}>
            {pending.length === 0 && (
              <Typography sx={{ color: C.textDim, textAlign: 'center', gridColumn: '1 / -1', py: 5, fontSize: '0.95rem' }}>
                {isDe ? 'Keine ausstehenden Abholungen' : 'No pending pickups'}
              </Typography>
            )}
            {pending.map(o => renderCard(o, 'pending'))}
          </Box>
        </Box>

        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.2, mb: 2 }}>
            <CheckIcon sx={{ color: C.served, fontSize: 26 }} />
            <Typography sx={{ color: C.text, fontWeight: 800, fontSize: '1.4rem' }}>
              {isDe ? 'WIRD BEDIENT' : 'BEING SERVED'}: {serving.length}
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }, gap: 1.5 }}>
            {serving.length === 0 && (
              <Typography sx={{ color: C.textDim, textAlign: 'center', gridColumn: '1 / -1', py: 5, fontSize: '0.95rem' }}>
                {isDe ? 'Niemand wird gerade bedient' : 'No one being served'}
              </Typography>
            )}
            {serving.map(o => renderCard(o, 'serving'))}
          </Box>
        </Box>
      </Box>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.sev} sx={{ backgroundColor: snack.sev === 'success' ? '#1b5e20' : '#b71c1c', color: '#fff' }}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PickupPanelPage;
